<template>
  <div style="overflow-y: auto; scrollbar-gutter: stable; max-height: 600px;">
    <div v-if="!specificGravitySensors.length" class="step-input">
      <p>No sensors need a specific gravity.</p>
    </div>
    <div v-else class="specific-gravity-section">
      <div class="specific-gravity-header d-flex justify-content-between align-items-center">
        <h5>Tank {{ activeIdx + 1 }} of {{ specificGravitySensors.length }}: {{ sensorName(currentSensor) }}</h5>

        <div class="button-group d-flex">
          <button class="btn btn-sm btn-outline-secondary" :disabled="!canGoPrev" @click="goPrev">
            Previous
          </button>
          <button class="btn btn-sm btn-outline-secondary ms-2" :disabled="!canGoNext || !isCurrentValid" @click="goNext">
            Next Sensor
          </button>
        </div>
      </div>

      <p class="text-muted">Enter the specific gravity of the liquid in this tank. Water is 1.</p>

      <div class="attr-row">
        <label class="attr-label">Specific Gravity</label>
        <input class="form-control-sm attr-input"
               type="number"
               min="0"
               step="0.01"
               :value="currentSpecificGravity"
               @input="setSpecificGravity($event.target.value)"
        />
        <div class="text-center text-muted" v-if="!isCurrentValid">
          <small>Enter a specific gravity greater than 0.</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {useStore} from "vuex";
import {DEFAULT_SPECIFIC_GRAVITY} from "@/store/ConfigWizard.module.js";

const store = useStore();
const activeIdx = ref(0);

const specificGravitySensors = computed(() => store.getters['ConfigWizard/specificGravitySensors']);

const currentEntry = computed(() => specificGravitySensors.value[activeIdx.value]);
const currentSensorIdx = computed(() => currentEntry.value?.sensorIdx);
const currentSensor = computed(() => currentEntry.value?.sensor);

const canGoPrev = computed(() => activeIdx.value > 0);
const canGoNext = computed(() => activeIdx.value < specificGravitySensors.value.length - 1);
const goPrev = () => { if (canGoPrev.value) activeIdx.value--; };
const goNext = () => { if (canGoNext.value) activeIdx.value++; };

// Undefined until the user types, so it shows as water
const specificGravity = (sensor) => {
  let value = sensor?.tank_config?.specific_gravity;
  return value === undefined ? DEFAULT_SPECIFIC_GRAVITY : value;
};

const currentSpecificGravity = computed(() => specificGravity(currentSensor.value) ?? '');
const setSpecificGravity = (value) => {
  store.commit('ConfigWizard/setSensorSpecificGravity', {
    sensorIdx: currentSensorIdx.value,
    specific_gravity: value === '' ? null : parseFloat(value),
  });
};

const isSensorValid = ({sensor} = {}) => {
  let value = specificGravity(sensor);
  return value != null && !isNaN(value) && value > 0;
};

const isCurrentValid = computed(() => isSensorValid(currentEntry.value));

const sensorName = (sensor) => {
  // Attribute 9150 is the Sensor Name
  let name = sensor?.attributes?.find(a => a.attribute_info_id === 9150)?.attribute_value;
  if (name != null) {
    return name;
  }
  if (sensor?.sensor_type_name == null || sensor?.modbus_id == null) {
    return 'Unnamed Sensor';
  }
  return `${sensor.sensor_type_name} ${sensor.modbus_id}`;
};

const validate = () => {
  for (let i = 0; i < specificGravitySensors.value.length; i++) {
    const {sensor} = specificGravitySensors.value[i];
    if (!isSensorValid(specificGravitySensors.value[i])) {
      activeIdx.value = i;
      return {valid: false, msg: `Please enter a specific gravity greater than 0 for ${sensorName(sensor)}.`};
    }
  }
  return {valid: true, msg: ''};
};

const save = async () => await store.dispatch('ConfigWizard/saveSpecificGravities');

// eslint-disable-next-line no-undef
defineExpose({validate, save});
</script>

<style scoped>
.specific-gravity-header {
  margin-bottom: 10px;
}

.specific-gravity-header h5 {
  margin: 0;
}

.button-group {
  display: flex;
  gap: 10px;
}

.text-muted {
  color: var(--color-txt) !important;
}

.attr-row {
  background-color: var(--color-bg);
  margin-bottom: 14px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
}

.attr-label {
  display: block;
  white-space: normal;
  margin-bottom: 6px;
}

.attr-input {
  text-align: center;
  width: 100%;
  background-color: #ffffff;
  color: #000000;
  border: 2px solid var(--color-border);
  border-radius: 4px;
  padding: 6px 8px;
}

.attr-input:focus {
  border-color: var(--active);
  outline: none;
  box-shadow: 0 0 0 2px var(--active);
}
</style>
